// src/Components/VendorDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api'; // Your API service

const VendorDashboard = () => {
    const [vendorsData, setVendorsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState(''); // For search functionality

    const fetchVendorDashboardData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // The /vendors/ endpoint should use the VendorSerializer with summary counts
            const response = await api.get('/vendors/');
            setVendorsData(response.data || []); // Ensure response.data is an array
        } catch (err) {
            console.error("Error fetching vendor dashboard data:", err);
            setError("Failed to load vendor data. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVendorDashboardData();
    }, [fetchVendorDashboardData]);

    const formatCountries = (countries) => {
        if (!countries) return '—';
        if (Array.isArray(countries) && countries.length > 0) {
            return countries.join(', ');
        }
        // If API ever sends a string for some vendors (defensive)
        return typeof countries === 'string' && countries.trim() !== '' ? countries : '—';
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        try {
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return '—';
            return d.toLocaleDateString();
        } catch {
            return '—';
        }
    };

    const filteredVendors = vendorsData.filter(vendor => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;

        const nameMatches = (vendor.name || '').toLowerCase().includes(term);

        // check countries array or string
        const countries = vendor.countries;
        const countriesMatches = Array.isArray(countries)
            ? countries.some(c => (c || '').toLowerCase().includes(term))
            : (countries || '').toLowerCase().includes(term);

        const countryField = formatCountries(countries).toLowerCase();
        const countryTextMatches = countryField.includes(term);

        return nameMatches || countriesMatches || countryTextMatches || (vendor.country || '').toLowerCase().includes(term);
    });

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-100">
                <div className="text-center">
                    <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-700">Loading Vendor Dashboard...</h2>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center p-10 bg-red-50 text-red-700 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-2">Error</h3>
                <p>{error}</p>
                <button 
                    onClick={fetchVendorDashboardData} 
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                <header className="mb-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900">Vendor Dashboard</h1>
                        <p className="mt-1 text-lg text-gray-600">Overview of vendor RFQ participation and awards.</p>
                    </div>
                    <Link
                        to="/vendors/new" // Link to create new vendor page
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Add New Vendor
                    </Link>
                </header>

                <div className="mb-6">
                    <input
                        type="text"
                        placeholder="Search vendors by name or country..."
                        className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {filteredVendors.length === 0 ? (
                    <div className="text-center py-10 bg-white rounded-lg shadow">
                        <p className="text-gray-500 italic">
                            {vendorsData.length === 0 ? "No vendors found." : "No vendors match your search criteria."}
                        </p>
                    </div>
                ) : (
                    <div className="bg-white shadow-xl rounded-lg overflow-x-auto border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Vendor Name
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Country
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Awarded RFQs
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Submitted RFQs (Total)
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created At
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {filteredVendors.map((vendor) => (
                                    <tr key={vendor.id} className="hover:bg-gray-50 transition-colors duration-150">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            <Link to={`/vendors/${vendor.id}`} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                                                {vendor.name}
                                            </Link>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatCountries(vendor.countries)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                (vendor.awarded_rfqs_count ?? 0) > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {vendor.awarded_rfqs_count ?? 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            <span className="px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {vendor.participated_rfqs_count ?? 0}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(vendor.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                                            <Link to={`/vendors/${vendor.id}`} className="text-indigo-600 hover:text-indigo-900">
                                                View
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VendorDashboard;
    